function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


/* Cosmos — mejoras de catálogo, marcas y variantes (borrador) */
(() => {
  const moveSoldOutLast = () => {
    document.querySelectorAll('#product-grid, [data-id="product-grid"]').forEach((container) => {
      const grid = container.matches('ul.product-grid') ? container : container.querySelector('ul.product-grid');
      container.classList.remove('cosmos-sold-out-card');
      if (!grid) return;
      if (grid.dataset.cosmosSorting === 'true') return;
      grid.dataset.cosmosSorting = 'true';
      const soldOut = [...grid.children].filter((item) =>
        /agotad[oa]/i.test(item.innerText || '')
      );
      const startIndex = grid.children.length - soldOut.length;
      const alreadyLast = soldOut.every((item, index) => grid.children[startIndex + index] === item);
      if (!alreadyLast) soldOut.forEach((item) => grid.appendChild(item));
      delete grid.dataset.cosmosSorting;
    });
  };

  const addBrandsHeading = () => {
    const section = document.querySelector('[id$="__ss_scrolling_logo_cloud_4_F6TnbE"]');
    if (!section || section.querySelector('.cosmos-brands-heading')) return;
    const heading = document.createElement('div');
    heading.className = 'cosmos-brands-heading';
    heading.innerHTML = '<h2>Marcas que forman parte de nuestro universo</h2>';
    section.prepend(heading);
  };

  const addVariantThumbnails = async () => {
    const productRoot = document.querySelector('product-info');
    if (!productRoot || productRoot.querySelector('.cosmos-variant-thumbnails')) return;

    const fieldsets = [...productRoot.querySelectorAll('fieldset')];
    const toneFieldset = fieldsets.find((fieldset) =>
      /tono|color|aroma/i.test(fieldset.querySelector('legend')?.textContent || '')
    );
    if (!toneFieldset) return;

    try {
      const product = await fetch(location.pathname + '.js').then((response) => response.json());
      const optionIndex = product.options.findIndex((option) =>
        /tono|color|aroma/i.test(typeof option === 'string' ? option : option.name)
      );
      if (optionIndex < 0) return;

      const values = new Map();
      product.variants.forEach((variant) => {
        const value = variant.options[optionIndex];
        if (!values.has(value)) values.set(value, variant);
        if (!values.get(value)?.featured_image && variant.featured_image) values.set(value, variant);
      });

      const picker = document.createElement('div');
      picker.className = 'cosmos-variant-thumbnails';
      picker.setAttribute('aria-label', 'Selecciona una variante');

      values.forEach((variant, value) => {
        const input = [...toneFieldset.querySelectorAll('input')].find((item) => item.value === value);
        if (!input) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cosmos-variant-thumb' + (input.checked ? ' is-active' : '');
        button.title = value;
        button.setAttribute('aria-label', value);

        if (variant.featured_image?.src) {
          const image = document.createElement('img');
          image.src = variant.featured_image.src + (variant.featured_image.src.includes('?') ? '&' : '?') + 'width=124';
          image.alt = value;
          image.loading = 'lazy';
          button.appendChild(image);
        }

        const label = document.createElement('span');
        label.textContent = value;
        button.appendChild(label);

        button.addEventListener('click', () => {
          input.click();
          picker.querySelectorAll('.cosmos-variant-thumb').forEach((item) =>
            item.classList.toggle('is-active', item === button)
          );
        });
        picker.appendChild(button);
      });

      if (picker.children.length > 1) toneFieldset.insertAdjacentElement('afterend', picker);
    } catch (error) {
      console.warn('Cosmos: no fue posible preparar las miniaturas de variantes.', error);
    }
  };

  const initializeCosmosEnhancements = () => {
    moveSoldOutLast();
    addBrandsHeading();
    addVariantThumbnails();

    const observer = new MutationObserver(() => {
      window.clearTimeout(window.cosmosEnhancementTimer);
      window.cosmosEnhancementTimer = window.setTimeout(moveSoldOutLast, 80);
    });
    document.querySelectorAll('#product-grid, [data-id="product-grid"]').forEach((container) => {
      const grid = container.matches('ul.product-grid') ? container : container.querySelector('ul.product-grid');
      if (grid) observer.observe(grid, { childList: true });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCosmosEnhancements);
  } else {
    initializeCosmosEnhancements();
  }
})();


(() => {
  const searchLink = (term) =>
    '/search?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(term);

  const groups = [
    ['Rostro', '/collections/rostro', [
      ['Bases', 'base maquillaje'], ['Correctores', 'corrector'], ['Rubores', 'rubor'],
      ['Contornos', 'contorno'], ['Iluminadores', 'iluminador'], ['Polvos', 'polvo maquillaje'],
      ['Primers', 'primer rostro'], ['Fijadores', 'fijador maquillaje']
    ]],
    ['Ojos', '/collections/ojos', [
      ['Delineadores', 'delineador ojos'], ['Sombras', 'sombras ojos'], ['Máscaras', 'mascara pestañas'],
      ['Pestañas', 'pestañas'], ['Cejas', 'cejas'], ['Glitter', 'glitter ojos']
    ]],
    ['Labios', '/collections/labios', [
      ['Labiales', 'labial'], ['Labiales líquidos', 'labial liquido'], ['Gloss', 'gloss'],
      ['Delineadores', 'delineador labios'], ['Bálsamos', 'balsamo labios']
    ]],
    ['Accesorios', '/collections/accesorios', [
      ['Brochas', 'brochas maquillaje'], ['Esponjas', 'esponja maquillaje'],
      ['Cosmetiqueras', 'cosmetiquera'], ['Herramientas', 'accesorios maquillaje'],
      ['Pegamento de pestañas', 'pegamento pestañas']
    ]]
  ];

  const buildMegaMenu = () => {
    const desktopDetails = [...document.querySelectorAll('.header__inline-menu details')].find((item) =>
      item.querySelector('summary')?.textContent.trim() === 'Maquillaje'
    );
    if (!desktopDetails) return;

    const submenu = desktopDetails.querySelector('.header__submenu');
    if (!submenu || submenu.classList.contains('cosmos-mega-menu')) return;

    submenu.classList.add('cosmos-mega-menu');
    submenu.innerHTML =
      '<li class="cosmos-mega-all"><a href="/collections/todos-los-productos">Ver todo maquillaje</a></li>' +
      groups.map(([title, href, links]) =>
        '<li class="cosmos-mega-column"><a class="cosmos-mega-title" href="' + href + '">' + title + '</a><ul>' +
        links.map(([label, query]) => '<li><a href="' + searchLink(query) + '">' + label + '</a></li>').join('') +
        '</ul></li>'
      ).join('');

    const positionMenu = () => {
      const header = document.querySelector('.header-wrapper');
      if (header) submenu.style.top = Math.round(header.getBoundingClientRect().bottom) + 'px';
    };
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildMegaMenu);
  } else {
    buildMegaMenu();
  }
})();


(() => {
  const updateBrandsLinks = () => {
    document.querySelectorAll('a').forEach((link) => {
      if (/ver todas las marcas/i.test(link.textContent || '')) {
        link.setAttribute('href', '/pages/marcas');
      }
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateBrandsLinks);
  } else {
    updateBrandsLinks();
  }
})();


/* Cosmos — persistencia de variantes y agotados visibles (2026-08-21) */
(() => {
  const soldOutPattern = /agotad[oa]/i;

  const refreshSoldOutCards = () => {
    document.querySelectorAll('#product-grid, [data-id="product-grid"]').forEach((container) => {
      const grid = container.matches('ul.product-grid') ? container : container.querySelector('ul.product-grid');
      container.classList.remove('cosmos-sold-out-card');
      if (!grid) return;
      if (grid.dataset.cosmosPersistentSorting === 'true') return;
      grid.dataset.cosmosPersistentSorting = 'true';
      const cards = [...grid.children];
      cards.forEach((item) => {
        item.classList.toggle('cosmos-sold-out-card', soldOutPattern.test(item.innerText || ''));
      });
      const available = cards.filter((item) => !item.classList.contains('cosmos-sold-out-card'));
      const soldOut = cards.filter((item) => item.classList.contains('cosmos-sold-out-card'));
      const desiredOrder = [...available, ...soldOut];
      const orderChanged = desiredOrder.some((item, index) => grid.children[index] !== item);
      if (orderChanged) desiredOrder.forEach((item) => grid.appendChild(item));
      delete grid.dataset.cosmosPersistentSorting;
    });
  };

  let productPromise;
  const getProductData = () => {
    productPromise ||= fetch(location.pathname + '.js').then((response) => {
      if (!response.ok) throw new Error('No fue posible consultar el producto');
      return response.json();
    });
    return productPromise;
  };

  let variantRefreshTimer;

  const hideNativeToneFieldset = (root = document.querySelector('product-info')) => {
    if (!root) return;
    [...root.querySelectorAll('fieldset')].forEach((fieldset) => {
      const isToneSelector = /tono|color|aroma/i.test(fieldset.querySelector('legend')?.textContent || '');
      fieldset.classList.toggle('cosmos-native-variant-picker', isToneSelector);
    });
  };

  const ensurePersistentVariantThumbnails = async () => {
    const productRoot = document.querySelector('product-info');
    if (!productRoot) return;

    const toneFieldset = [...productRoot.querySelectorAll('fieldset')].find((fieldset) =>
      /tono|color|aroma/i.test(fieldset.querySelector('legend')?.textContent || '')
    );
    if (!toneFieldset) return;
    toneFieldset.classList.add('cosmos-native-variant-picker');

    const selectedValue = toneFieldset.querySelector('input:checked')?.value;
    let picker = productRoot.querySelector('.cosmos-variant-thumbnails');

    if (picker?.dataset.cosmosPersistent === 'true' && picker.previousElementSibling === toneFieldset) {
      picker.querySelectorAll('.cosmos-variant-thumb').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.value === selectedValue);
        button.setAttribute('aria-pressed', button.dataset.value === selectedValue ? 'true' : 'false');
      });
      return;
    }

    picker?.remove();

    try {
      const product = await getProductData();
      const optionIndex = product.options.findIndex((option) =>
        /tono|color|aroma/i.test(typeof option === 'string' ? option : option.name)
      );
      if (optionIndex < 0) return;

      const variantsByValue = new Map();
      product.variants.forEach((variant) => {
        const value = variant.options[optionIndex];
        const current = variantsByValue.get(value);
        if (!current || (!current.featured_image && variant.featured_image)) {
          variantsByValue.set(value, variant);
        }
      });

      picker = document.createElement('div');
      picker.className = 'cosmos-variant-thumbnails';
      picker.dataset.cosmosPersistent = 'true';
      picker.setAttribute('role', 'group');
      picker.setAttribute('aria-label', 'Selecciona una variante');

      variantsByValue.forEach((variant, value) => {
        const currentFieldset = [...productRoot.querySelectorAll('fieldset')].find((fieldset) =>
          /tono|color|aroma/i.test(fieldset.querySelector('legend')?.textContent || '')
        );
        const input = [...(currentFieldset?.querySelectorAll('input') || [])].find((item) => item.value === value);
        if (!input) return;

        const valueAvailable = product.variants.some(
          (item) => item.options[optionIndex] === value && item.available
        );

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cosmos-variant-thumb';
        button.dataset.value = value;
        button.title = valueAvailable ? value : value + ' — Agotado';
        button.setAttribute('aria-label', valueAvailable ? value : value + ' — Agotado');
        button.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
        button.classList.toggle('is-active', input.checked);
        button.classList.toggle('is-sold-out', !valueAvailable);

        if (variant.featured_image?.src) {
          const image = document.createElement('img');
          image.src = variant.featured_image.src + (variant.featured_image.src.includes('?') ? '&' : '?') + 'width=124';
          image.alt = value;
          image.loading = 'lazy';
          button.appendChild(image);
        }

        const label = document.createElement('span');
        label.className = 'cosmos-variant-name';
        label.textContent = value;
        button.appendChild(label);

        if (!valueAvailable) {
          const stockBadge = document.createElement('small');
          stockBadge.className = 'cosmos-variant-stock-badge';
          stockBadge.textContent = 'Agotado';
          button.appendChild(stockBadge);
        }

        button.addEventListener('click', () => {
          const latestFieldset = [...productRoot.querySelectorAll('fieldset')].find((fieldset) =>
            /tono|color|aroma/i.test(fieldset.querySelector('legend')?.textContent || '')
          );
          const latestInput = [...(latestFieldset?.querySelectorAll('input') || [])].find((item) => item.value === value);
          latestInput?.click();
          window.clearTimeout(variantRefreshTimer);
          variantRefreshTimer = window.setTimeout(ensurePersistentVariantThumbnails, 180);
        });

        picker.appendChild(button);
      });

      const latestFieldset = [...productRoot.querySelectorAll('fieldset')].find((fieldset) =>
        /tono|color|aroma/i.test(fieldset.querySelector('legend')?.textContent || '')
      );
      if (picker.children.length > 1 && latestFieldset) {
        latestFieldset.insertAdjacentElement('afterend', picker);
      }
    } catch (error) {
      console.warn('Cosmos: no fue posible mantener las miniaturas de variantes.', error);
    }
  };

  const scheduleVariantRefresh = () => {
    hideNativeToneFieldset();
    window.clearTimeout(variantRefreshTimer);
    variantRefreshTimer = window.setTimeout(ensurePersistentVariantThumbnails, 100);
  };

  const initializePersistentEnhancements = () => {
    refreshSoldOutCards();
    hideNativeToneFieldset();
    ensurePersistentVariantThumbnails();

    const productRoot = document.querySelector('product-info');
    if (productRoot) {
      productRoot.addEventListener('change', scheduleVariantRefresh, true);
      new MutationObserver(scheduleVariantRefresh).observe(productRoot, { childList: true, subtree: true });
    }

    new MutationObserver(() => {
      window.clearTimeout(window.cosmosSoldOutRefreshTimer);
      window.cosmosSoldOutRefreshTimer = window.setTimeout(refreshSoldOutCards, 80);
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePersistentEnhancements);
  } else {
    initializePersistentEnhancements();
  }
})();


/* Cosmos — evitar selectores de variantes duplicados (2026-08-21) */
(() => {
  let cleanupTimer;
  const keepOneVariantPicker = () => {
    const productRoot = document.querySelector('product-info');
    if (!productRoot) return;
    const pickers = [...productRoot.querySelectorAll('.cosmos-variant-thumbnails')];
    if (pickers.length < 2) return;

    const keep = pickers.find((picker) => picker.dataset.cosmosPersistent === 'true') || pickers[0];
    pickers.forEach((picker) => {
      if (picker !== keep) picker.remove();
    });

    const seen = new Set();
    keep.querySelectorAll('.cosmos-variant-thumb').forEach((button) => {
      const key = button.dataset.value || button.textContent.trim();
      if (seen.has(key)) button.remove();
      else seen.add(key);
    });
  };

  const scheduleCleanup = () => {
    window.clearTimeout(cleanupTimer);
    cleanupTimer = window.setTimeout(keepOneVariantPicker, 140);
  };

  const start = () => {
    keepOneVariantPicker();
    const productRoot = document.querySelector('product-info');
    if (productRoot) {
      new MutationObserver(scheduleCleanup).observe(productRoot, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();


/* Cosmos — mega menú de Skincare (2026-08-21) */
(() => {
  const searchLink = (term) =>
    '/search?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(term);

  const groups = [
    ['Rutina facial', '/collections/skincare', [
      ['Limpiadores', '/collections/limpiadores-faciales', true],
      ['Agua micelar', 'agua micelar'],
      ['Tónicos', '/collections/tonicos-faciales', true],
      ['Esencias', '/collections/esencias-faciales', true],
      ['Sérums', '/collections/serums-faciales', true],
      ['Cremas e hidratantes', '/collections/hidratantes-faciales', true],
      ['Mascarillas', '/collections/mascarillas-faciales', true],
      ['Protector solar', '/collections/protectores-solares', true]
    ]],
    ['Necesidades', '/collections/skincare', [
      ['Acné e imperfecciones', '/collections/acne-e-imperfecciones', true],
      ['Manchas y luminosidad', '/collections/anti-manchas-luminosidad', true],
      ['Hidratación', '/collections/hidratacion-facial', true],
      ['Poros y textura', '/collections/poros-y-textura', true],
      ['Antiedad', '/collections/antiedad-facial', true],
      ['Piel sensible', '/collections/piel-sensible-delicada', true],
      ['Contorno de ojos', '/collections/contorno-de-ojos', true]
    ]],
    ['Tipo de piel', '/collections/skincare', [
      ['Grasa y mixta', '/collections/piel-grasa-mixta', true],
      ['Seca y deshidratada', '/collections/piel-seca-deshidratada', true],
      ['Sensible y delicada', '/collections/piel-sensible-delicada', true],
      ['Todo tipo de piel', '/collections/skincare', true]
    ]],
    ['Descubre', '/collections/skincare-coreano-k-beauty', [
      ['Skincare coreano (K-Beauty)', '/collections/skincare-coreano-k-beauty', true],
      ['Rutinas completas', 'rutina skincare'],
      ['Cuidado de labios', '/collections/cuidado-de-labios', true],
      ['Cuidado corporal', '/collections/cuidado-corporal', true],
      ['Nuevos ingresos', '/collections/recien-llegados-%F0%9F%86%95%F0%9F%94%A5', true]
    ]]
  ];

  const buildSkincareMegaMenu = () => {
    const desktopDetails = [...document.querySelectorAll('.header__inline-menu details')].find((item) =>
      item.querySelector('summary')?.textContent.trim() === 'Skincare'
    );
    if (!desktopDetails) return;

    const submenu = desktopDetails.querySelector('.header__submenu');
    if (!submenu || submenu.classList.contains('cosmos-skincare-mega-menu')) return;

    submenu.classList.add('cosmos-mega-menu', 'cosmos-skincare-mega-menu');
    submenu.innerHTML =
      '<li class="cosmos-mega-all"><a href="/collections/skincare">Ver todo skincare</a></li>' +
      groups.map(([title, href, links]) =>
        '<li class="cosmos-mega-column"><a class="cosmos-mega-title" href="' + href + '">' + title + '</a><ul>' +
        links.map(([label, destination, direct]) =>
          '<li><a href="' + (direct ? destination : searchLink(destination)) + '">' + label + '</a></li>'
        ).join('') +
        '</ul></li>'
      ).join('');

    const positionMenu = () => {
      const header = document.querySelector('.header-wrapper');
      if (header) submenu.style.top = Math.round(header.getBoundingClientRect().bottom) + 'px';
    };
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildSkincareMegaMenu);
  } else {
    buildSkincareMegaMenu();
  }
})();


/* Cosmos — preparación de logos de marcas (2026-08-21) */
(() => {
  const prepareBrandLogos = () => {
    if (location.pathname !== '/pages/marcas') return;
    const cards = [...document.querySelectorAll('.cosmos-brand-card')];
    const darkHandles = new Set([
      '/collections/ananda',
      '/collections/arantza-cosmetics',
      '/collections/atenea',
      '/collections/pixi',
      '/collections/prosa',
      '/collections/true-lashes',
      '/collections/true-makeup'
    ]);
    cards.forEach((card) => {
      const image = card.querySelector('.cosmos-brand-logo img');
      if (!image) return;
      image.removeAttribute('width');
      image.removeAttribute('height');
      if (darkHandles.has(card.getAttribute('href'))) image.classList.add('cosmos-logo-force-dark');
    });
    const cosmos = document.querySelector('.cosmos-brand-card[href="/collections/articulos-cosmos"] .cosmos-brand-logo img');
    if (cosmos) {
      cosmos.src = '//cosmosbeautyshop.com/cdn/shop/files/0e468bb8-4e10-4d78-b1a9-5bb7d6deef66.png?v=1782758159&width=600';
      cosmos.srcset = '';
      cosmos.alt = 'Logotipo de Cosmos Beauty Shop';
      cosmos.classList.add('cosmos-logo-large');
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prepareBrandLogos);
  else prepareBrandLogos();
})();
